import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { VelavuAPI, VelavuGeofence } from "velavu-js-api";
import IconDelete from "../../../../dynamicicons/icon-delete";
import IconEdit from "../../../../dynamicicons/icon-edit";
import DropdownPanelToolbar from "../../../../elements/dropdown-panel-toolbar";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../../../elements/velavu-menu";
import { useVelavuModal } from "../../../../elements/velavu-modal";
import VelavuAlertModal from "../../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuButton from "../../../../elements/velavu-button";
import { useGeocoder } from "../../../../helper/api-helper";
import PinnedList from "../pinned-list";
import * as styles from "./geofence-detail.module.scss";
import VelavuTabSwitcher from "../../../../elements/velavu-tab-switcher";
import GeofenceDetailAssets from "./geofence-detail-assets";
import GeofenceDetailEvents from "./geofence-detail-events";
import VelavuAlertModalObject from "../../../../elements/velavu-alert-modal/velavu-alert-modal-object";
import { useHasPermission } from "../../../sign-in-context/sign-in-context";

export interface GeofenceDetailProps {
	geofence: VelavuGeofence;
	onBack?: () => void;
	onClose?: () => void;

	onEdit?: () => void;
	onDelete?: () => void;

	onSelectDevice?: (deviceID: string) => void;
	onHoverDevice?: (deviceID: string | undefined) => void;
}

enum GeofenceDetailsTab {
	Assets,
	Events,
}

export default function GeofenceDetail(props: GeofenceDetailProps) {
	const { t } = useTranslation();

	const canWriteGeofences = useHasPermission("geofences:write");
	const canDeleteGeofences = useHasPermission("geofences:delete");
	const showGeofenceMenu = canWriteGeofences || canDeleteGeofences;

	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) =>
			setMenuAnchor(event.currentTarget),
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => setMenuAnchor(null), [setMenuAnchor]);

	const { pushModal } = useVelavuModal();
	const propsGeofence = props.geofence;
	const propsOnEdit = props.onEdit;
	const handleEditGeofence = useCallback(() => {
		closeMenu();
		if (propsOnEdit !== undefined) propsOnEdit();
	}, [closeMenu, propsOnEdit]);
	const propsOnDelete = props.onDelete;
	const handleDeleteGeofence = useCallback(() => {
		closeMenu();
		pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("dashboard.geofence_detail.delete.title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalObject>
					{propsGeofence.name}
				</VelavuAlertModalObject>

				<VelavuAlertModalContent>
					{t("dashboard.geofence_detail.delete.body")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.delete")}
						variant="danger"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		)).then((result) => {
			if (result) {
				VelavuAPI.geofences
					.deleteExistingGeofence(propsGeofence.id)
					.catch(() =>
						alert(
							t("dashboard.geofence_detail.delete.failed", {
								id: propsGeofence.id,
							}),
						),
					);
				if (propsOnDelete !== undefined) propsOnDelete();
			}
		});
	}, [closeMenu, pushModal, propsGeofence, propsOnDelete, t]);

	const address = useGeocoder(propsGeofence.center);

	const [selectedTab, setSelectedTab] = useState(GeofenceDetailsTab.Assets);

	return (
		<React.Fragment>
			{showGeofenceMenu && (
				<VelavuMenu
					open={!!menuAnchor}
					onClose={closeMenu}
					refPosition={menuAnchor}
				>
					{canWriteGeofences && (
						<VelavuMenuItem
							icon={(size, color) => (
								<IconEdit size={size} color={color} />
							)}
							label={t("common.edit_geofence")}
							onClick={handleEditGeofence}
						/>
					)}
					{canDeleteGeofences && (
						<VelavuMenuItem
							icon={(size, color) => (
								<IconDelete size={size} color={color} />
							)}
							label={t("common.delete_geofence")}
							onClick={handleDeleteGeofence}
							danger
						/>
					)}
				</VelavuMenu>
			)}

			<PinnedList>
				<DropdownPanelToolbar
					onBack={props.onBack}
					onClose={props.onClose}
				/>

				<div className={styles.titleHeader}>
					<div className={styles.titleText}>
						<span className={styles.titleHeaderTitle}>
							{propsGeofence.name}
						</span>
						<span className={styles.titleHeaderSubtitle}>
							{address}
						</span>
					</div>

					{showGeofenceMenu && (
						<VelavuOverflowButton
							onClick={openMenu}
							open={!!menuAnchor}
						/>
					)}
				</div>

				<VelavuTabSwitcher
					selectedKey={selectedTab}
					onSelectKey={setSelectedTab}
					labels={{
						[GeofenceDetailsTab.Assets]: t("dashboard.tabs.assets"),
						[GeofenceDetailsTab.Events]: t("dashboard.tabs.events"),
					}}
				>
					{selectedTab === GeofenceDetailsTab.Assets && (
						<GeofenceDetailAssets
							geofenceID={props.geofence.id}
							onSelectDevice={props.onSelectDevice}
							onHoverDevice={props.onHoverDevice}
						/>
					)}

					{selectedTab === GeofenceDetailsTab.Events && (
						<GeofenceDetailEvents geofenceID={props.geofence.id} />
					)}
				</VelavuTabSwitcher>
			</PinnedList>
		</React.Fragment>
	);
}
